// Material Dashboard 2 React context
import { useMaterialUIController } from "context";

// Images
import masterCardLogo from "assets/images/logos/mastercard.png";
import visaLogo from "assets/images/logos/visa.png";

// @mui material components
import {
  Snackbar,
  Alert,
  AlertTitle,
  Grid,
  Card,
  Icon,
  Tooltip,
  CircularProgress,
  Backdrop,
  Dialog,
  DialogContent,
  DialogContentText,
  DialogTitle,
  DialogActions,
  MenuItem,
  InputLabel,
  Select,
  FormControl,
} from "@mui/material";

// Material Dashboard 2 React components
import MDBox from "theme/components/MDBox";
import MDTypography from "theme/components/MDTypography";
import MDInput from "theme/components/MDInput";
import MDButton from "theme/components/MDButton";

// Material Dashboard 2 React example components
import DashboardLayout from "app/LayoutContainers/DashboardLayout";
import DashboardNavbar from "app/Navbars/DashboardNavbar";
import Footer from "theme/Footer";

// import { useAuthUser, useAuthHeader } from "react-auth-kit";

import BalanceOverviewCard from "app/balances/balance/BalanceOverviewCard";
import Transaction from "app/balances/Transaction";
import { useEffect, useState } from "react";
import axios from "axios.js";
import { useParams, useNavigate, Link } from "react-router-dom";
import usePageTitle from "app/pageTitle";
import CompanyNotification from "app/company_notification";

// import { confirm } from "react-confirm-box";

function Balance() {
  const { currency } = useParams();

  const pageTitle = `${currency} Balance`;
  usePageTitle(pageTitle);

  const [controller] = useMaterialUIController();
  const { darkMode } = controller;

  let formData;

  const navigate = useNavigate();

  useEffect(() => {
    if (currency === null) {
      // console.log("NULL");
      navigate("/dashboard");
    }
  }, []);

  function emptyCache() {
    if ("caches" in window) {
      caches.keys().then((names) => {
        // Delete all the cache files
        names.forEach((name) => {
          caches.delete(name);
        });
      });
    }
  }

  function versionRefresh() {
    emptyCache();
    window.location.reload();
  }

  function appVersionCheck(newVersion) {
    // Handle App Update
    const currentVersion = localStorage.getItem("_al_version_");
    let shouldUpdate = false;
    if (currentVersion !== newVersion) {
      shouldUpdate = true;
    }
    localStorage.setItem("_al_version_", newVersion);

    if (shouldUpdate) {
      if (
        window.confirm(
          "A new version is available. Alero will automatically refresh to get the latest update."
        ) === true
      ) {
        versionRefresh();
      } else {
        versionRefresh();
      }
    }
  }

  const [refreshPage, setRefreshPage] = useState(false);
  const [showCards, setShowCards] = useState(false);

  const [isVerified, setIsVerified] = useState(true);

  const [balance, setBalance] = useState(null);
  // const [credit, setCredit] = useState(null);
  // const [debit, setDebit] = useState(null);
  const [vbanks, setVbanks] = useState([]);
  const [vcards, setVcards] = useState([]);
  const [transactions, setTransactions] = useState(null);
  const [currencySettings, setCurrencySettings] = useState(null);
  const [commericals, setCommercials] = useState(null);
  const [currencies, setCurrencies] = useState([]);
  const [exchangeRates, setExchangeRates] = useState([]);
  const [conversionFee, setConversionFee] = useState(null);
  const [vCardWithdrawalFee, setVCardWithdrawalFee] = useState(0);
  const [vCardFundingFeeObject, setVCardFundingFeeObject] = useState(null);

  const [showCompanyNotification, setShowCompanyNotification] = useState(false);
  const [companyNotification, setCompanyNotification] = useState(null);

  const [showLoading, setShowLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [errorMsg, setErrMsg] = useState("");
  const [severity, setSeverity] = useState("");
  const handleClose = () => {
    setOpen(false);
    setErrMsg("");
  };

  useEffect(() => {
    const fetchData = async () => {
      setShowLoading(true);
      await axios
        .get(`users/balance/${currency}.json`)
        .then((result) => {
          setShowLoading(false);
          // console.log(result);
          if (result.data.status === "success") {
            setBalance(result.data.data.overview.balance);
            setIsVerified(result.data.data.verified);
            // setCredit(result.data.data.overview.credit);
            // setDebit(result.data.data.overview.debit);
            setTransactions(result.data.data.overview.entries);
            setVbanks(result.data.data.overview.virtual_accounts);

            setVcards(result.data.data.overview.virtual_cards);
            setShowCards(false);

            setCurrencySettings(result.data.data.overview.currency);
            setCommercials(result.data.data.overview.commercials);
            setCurrencies(result.data.data.overview.currencies);
            setExchangeRates(result.data.data.overview.exchange_rates);
            setConversionFee(result.data.data.overview.conversion_fee);
            setVCardWithdrawalFee(result.data.data.overview.vcard_withdrawal_fee);
            setVCardFundingFeeObject(result.data.data.overview.vcard_funding_fee);

            if (result.data.notification) {
              setCompanyNotification(result.data.notification);
              if (result.data.notification.pages.includes("ALL")) {
                setShowCompanyNotification(true);
              } else if (result.data.notification.pages.includes("balance")) {
                setShowCompanyNotification(true);
              }
            }
          } else {
            setErrMsg(result.data.data.message);
            setOpen(true);
          }

          appVersionCheck(result.data.version);
        })
        .catch(() => {
          setShowLoading(false);
          setErrMsg("An error occurred. Make sure you have an active internet connection.");
          setOpen(true);
        });
    };

    fetchData();
  }, [refreshPage]);

  const [openNewBankDialog, setOpenNewBankDialog] = useState(false);
  const [addressHouseNumber, setAddressHouseNumber] = useState(null);
  const [addressStreet, setAddressStreet] = useState(null);
  const [addressCity, setAddressCity] = useState(null);
  const [addressState, setAddressState] = useState(null);
  const [addressPostalCode, setAddressPostalCode] = useState(null);
  const [addressCountry, setAddressCountry] = useState(null);
  const [addressFile, setAddressFile] = useState(null);
  const [addressFileType, setAddressFileType] = useState(null);
  const [documentType, setDocumentType] = useState(null);
  const [documentNumber, setDocumentNumber] = useState(null);
  const [documentIssDate, setDocumentIssDate] = useState(null);
  const [documentExpDate, setDocumentExpDate] = useState(null);
  const [documentFile, setDocumentFile] = useState(null);
  const [documentFile2, setDocumentFile2] = useState(null);

  const [documentNumberText, setDocumentNumberText] = useState("ID Number");

  useEffect(() => {
    if (documentType === "dl") {
      setDocumentNumberText("License Number");
    } else if (documentType === "passport") {
      setDocumentNumberText("Passport Number");
    } else if (documentType === "nin") {
      setDocumentNumberText("NIN Number");
    } else {
      setDocumentNumberText("ID Number");
    }
  }, [documentType]);

  async function postDataNormal() {
    const json = { currency: `${currency}` };
    await axios
      .post("users/vbank/request.json", json)
      .then((result) => {
        setShowLoading(false);
        if (result.data.status === "success") {
          setSeverity("success");
          setErrMsg(result.data.data.message);
          setOpen(true);
          setRefreshPage(!refreshPage);
        } else {
          setSeverity("error");
          setErrMsg(result.data.data.message);
          setOpen(true);
        }
      })
      .catch(() => {
        setShowLoading(false);
        setSeverity("error");
        setErrMsg("An error occurred. Make sure you have an active internet connection.");
        setOpen(true);
      });
  }

  async function postDataMultipart() {
    formData = new FormData();
    formData.append("address_file_type", addressFileType);
    formData.append("address_file", addressFile);
    formData.append("currency", currency);
    formData.append("address_number", addressHouseNumber);
    formData.append("address_street", addressStreet);
    formData.append("address_city", addressCity);
    formData.append("address_state", addressState);
    formData.append("address_postal_code", addressPostalCode);
    formData.append("address_country", addressCountry);
    formData.append("document_type", documentType);
    formData.append("document_number", documentNumber);
    formData.append("document_iss_date", documentIssDate);
    formData.append("document_exp_date", documentExpDate);
    formData.append("document_file", documentFile);
    formData.append("document_file_2", documentFile2);

    // console.log(formData);
    await axios
      .post("users/vbank/request.json", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      })
      .then((result) => {
        setShowLoading(false);
        if (result.data.status === "success") {
          setSeverity("success");
          setErrMsg(result.data.data.message);
          setOpen(true);
          setRefreshPage(!refreshPage);
        } else {
          setSeverity("error");
          setErrMsg(result.data.data.message);
          setOpen(true);
        }
      })
      .catch(() => {
        setShowLoading(false);
        setSeverity("error");
        setErrMsg("An error occurred.");
        setOpen(true);
      });
  }

  async function requestAccount() {
    if (!currencySettings.vbank_enabled) {
      setSeverity("error");
      setErrMsg(`Virtual bank accounts are not enabled for ${currency}`);
      setOpen(true);
      return;
    }

    if (currency !== "NGN") {
      if (addressHouseNumber === null || addressHouseNumber.length === 0) {
        setSeverity("error");
        setErrMsg("Enter your address house number");
        setOpen(true);
        return;
      }

      if (addressStreet === null || addressStreet.length < 10) {
        setSeverity("error");
        setErrMsg("Enter your address street.");
        setOpen(true);
        return;
      }

      if (addressCity === null || addressCity.length < 3) {
        setSeverity("error");
        setErrMsg("Enter your address city");
        setOpen(true);
        return;
      }

      if (addressState === null || addressState.length < 3) {
        setSeverity("error");
        setErrMsg("Enter your address state");
        setOpen(true);
        return;
      }

      if (addressPostalCode === null || addressPostalCode.length === 0) {
        setSeverity("error");
        setErrMsg("Enter your address postal code or zip");
        setOpen(true);
        return;
      }

      if (addressCountry === null || addressCountry.length === 0) {
        setSeverity("error");
        setErrMsg("Enter your address country");
        setOpen(true);
        return;
      }

      if (documentType === null || documentType.length === 0) {
        setSeverity("error");
        setErrMsg("Select an ID document type");
        setOpen(true);
        return;
      }

      if (documentNumber === null || documentNumber.length === 0) {
        setSeverity("error");
        setErrMsg("Enter the ID document number as shown on file");
        setOpen(true);
        return;
      }

      if (["passport", "dl"].includes(documentType)) {
        if (documentIssDate === null || documentIssDate.length === 0) {
          setSeverity("error");
          setErrMsg("Enter ID document issue date");
          setOpen(true);
          return;
        }

        if (documentExpDate === null || documentExpDate.length === 0) {
          setSeverity("error");
          setErrMsg("Enter ID document expiry date");
          setOpen(true);
          return;
        }
      }

      if (["nin"].includes(documentType)) {
        if (documentIssDate === null || documentIssDate.length === 0) {
          setSeverity("error");
          setErrMsg("Enter NIN registration date");
          setOpen(true);
          return;
        }
      }

      if (documentFile === null) {
        setSeverity("error");
        setErrMsg("Upload a clear copy of your ID document.");
        setOpen(true);
        return;
      }

      if (["pvc", "dl"].includes(documentType)) {
        if (documentFile2 === null) {
          setSeverity("error");
          setErrMsg("Upload a clear copy of the back of your ID document.");
          setOpen(true);
          return;
        }
      }

      if (addressFileType === null) {
        setSeverity("error");
        setErrMsg("Proof of address file type is required");
        setOpen(true);
        return;
      }

      if (addressFile === null) {
        setSeverity("error");
        setErrMsg("Upload your utility bill or bank statement not more than 3 months old.");
        setOpen(true);
        return;
      }
    }

    setOpenNewBankDialog(false);
    setShowLoading(true);
    if (currency === "NGN") {
      postDataNormal();
    } else {
      postDataMultipart();
    }
  }

  const handleFileInputChange = (event, type) => {
    event.persist();
    const fileTypes = ["pdf", "png", "jpeg", "jpg"];
    const file = event.target.files[0];
    const extension = file.name.split(".").pop().toLowerCase();
    const supported = fileTypes.indexOf(extension) > -1;
    if (!supported) {
      setSeverity("error");
      setErrMsg("Only PDF, JPEG, PNG and JPG files accepted.");
      setOpen(true);
      return;
    }

    if (file) {
      const reader = new FileReader();
      reader.onload = function () {
        if (type === "A") {
          setAddressFile(file);
        } else if (type === "DF") {
          setDocumentFile(file);
        } else {
          setDocumentFile2(file);
        }
      };

      reader.onerror = function (ex) {
        console.log(ex);
      };

      reader.readAsBinaryString(file);
    } else {
      setAddressFile(null);
      event.target.value = null;
    }
  };

  const [cardAlias, setCurrentCardAlias] = useState(null);
  const [openNewCardDialog, setOpenNewCardDialog] = useState(false);
  const [openTransferTypeDialog, setOpenTransferTypeDialog] = useState(false);

  async function requestCard() {
    if (!currencySettings.vcard_enabled) {
      setSeverity("error");
      setErrMsg(`Virtual cards are not enabled for ${currency}`);
      setOpen(true);
      return;
    }

    if (cardAlias === null || cardAlias === "") {
      setSeverity("error");
      setErrMsg("Enter a card alias/nickname");
      setOpen(true);
      return;
    }

    setOpenNewCardDialog(false);
    setShowLoading(true);
    const json = { currency: `${currency}`, alias: cardAlias };
    await axios
      .post("users/vcard/request.json", json)
      .then((result) => {
        setShowLoading(false);
        if (result.data.status === "success") {
          setSeverity("success");
          setErrMsg("Card request submitted successfully. This may take a few moments to process.");
          setOpen(true);

          setRefreshPage(!refreshPage);
        } else {
          setSeverity("error");
          setErrMsg(result.data.data.message);
          setOpen(true);
        }
      })
      .catch(() => {
        setShowLoading(false);
        setSeverity("error");
        setErrMsg("An error occurred. Make sure you have an active internet connection.");
        setOpen(true);
      });
  }

  async function fundCard(reference, fundAmount) {
    setShowLoading(true);
    const json = { reference: `${reference}`, amount: fundAmount * 100 };
    await axios
      .post("users/vcard/fund.json", json)
      .then((result) => {
        setShowLoading(false);
        if (result.data.status === "success") {
          setSeverity("success");
          setErrMsg(result.data.data.message);
          setOpen(true);

          setRefreshPage(!refreshPage);
        } else {
          setSeverity("error");
          setErrMsg(result.data.data.message);
          setOpen(true);
        }
      })
      .catch(() => {
        setShowLoading(false);
        setSeverity("error");
        setErrMsg("An error occurred. Make sure you have an active internet connection.");
        setOpen(true);
      });
  }

  async function cardWithdraw(reference, fundAmount) {
    setShowLoading(true);
    const json = { reference: `${reference}`, amount: fundAmount * 100 };
    await axios
      .post("users/vcard/withdraw.json", json)
      .then((result) => {
        setShowLoading(false);
        if (result.data.status === "success") {
          setSeverity("success");
          setErrMsg(result.data.data.message);
          setOpen(true);

          setRefreshPage(!refreshPage);
        } else {
          setSeverity("error");
          setErrMsg(result.data.data.message);
          setOpen(true);
        }
      })
      .catch(() => {
        setShowLoading(false);
        setSeverity("error");
        setErrMsg("An error occurred. Make sure you have an active internet connection.");
        setOpen(true);
      });
  }

  const [currentCardReference, setCurrentCardReference] = useState(null);
  const [currentAmount, setCurrentAmount] = useState(0);
  const [openFundDialog, setOpenFundDialog] = useState(false);
  const [openWithdrawDialog, setOpenWithdrawDialog] = useState(false);

  const handleNewCardDialogClose = () => {
    setCurrentCardAlias(null);
    setOpenNewCardDialog(false);
  };

  const handleOpenFundDialog = (reference) => {
    setCurrentCardReference(reference);
    setOpenFundDialog(true);
  };

  const handleFundDialogClose = () => {
    setCurrentCardReference(null);
    setOpenFundDialog(false);
  };

  const handleOpenWithdrawDialog = (reference) => {
    setCurrentCardReference(reference);
    setOpenWithdrawDialog(true);
  };

  const handleWithdrawDialogClose = () => {
    setCurrentCardReference(null);
    setOpenWithdrawDialog(false);
  };

  function handleFundCardSubmit(event) {
    event.preventDefault();
    if (currentAmount <= 0) {
      setSeverity("error");
      setErrMsg("Enter a valid amount.");
      setOpen(true);
      return;
    }
    handleFundDialogClose();
    fundCard(currentCardReference, currentAmount);
  }

  function handleWithdrawCardSubmit(event) {
    event.preventDefault();
    if (currentAmount <= 0) {
      setSeverity("error");
      setErrMsg("Enter a valid amount.");
      setOpen(true);
      return;
    }
    handleWithdrawDialogClose();
    cardWithdraw(currentCardReference, currentAmount);
  }

  const [openCancelDialog, setCancelDialog] = useState(false);
  const [openVCardDialog, setVCardDialog] = useState(false);
  const [openDepositDialog, setDepositDialog] = useState(false);
  const [openVBankDialog, setVBankDialog] = useState(false);
  const [openDepositOptionsDialog, setOpenDepositOptionsDialog] = useState(false);

  async function terminateCard() {
    setShowLoading(true);
    const json = { reference: `${currentCardReference}` };
    await axios
      .post("users/vcard/terminate.json", json)
      .then((result) => {
        setShowLoading(false);
        if (result.data.status === "success") {
          setSeverity("success");
          setErrMsg("Card terminated successfully.");
          setOpen(true);

          setRefreshPage(!refreshPage);
        } else {
          setSeverity("error");
          setErrMsg(result.data.data.message);
          setOpen(true);
        }
      })
      .catch(() => {
        setShowLoading(false);
        setSeverity("error");
        setErrMsg("An error occurred. Make sure you have an active internet connection.");
        setOpen(true);
      });
  }

  const [amountPlaceholder, setAmountPlaceholder] = useState("Enter Amount");
  const [amountPlaceholder1, setAmountPlaceholder1] = useState("Enter Amount");
  const [conversionAmount, setConversionAmount] = useState("0.00");
  const [targetAmount, setTargetAmount] = useState("0.00");
  const [sourceCurrency, setSourceCurrency] = useState(null);
  const [destinationCurrency, setDestinationCurrency] = useState(null);
  const [openConversionTypeDialog, setOpenConversionTypeDialog] = useState(false);
  const [openConversionDialog, setOpenConversionDialog] = useState(false);
  const [calculated, setCalculated] = useState(false);
  const [rateLine, setRateLine] = useState(null);
  const [transactionFee, setTransactionFee] = useState(null);
  // const [destinationAmount, setDestinationAmount] = useState(null);
  const [convertButtonText, setConvertButtonText] = useState("Calculate");

  useEffect(() => {
    if (destinationCurrency != null) {
      setAmountPlaceholder1(`${destinationCurrency} Amount`);
    }

    if (sourceCurrency != null) {
      setAmountPlaceholder(`${sourceCurrency} Amount`);
    }
    setConversionAmount("0.00");
    setTargetAmount("0.00");
  }, [destinationCurrency, sourceCurrency]);

  function resetSwapForm() {
    setDestinationCurrency(null);
    setSourceCurrency(null);
    setConversionAmount("0.00");
    setTargetAmount("0.00");
    setCalculated(false);
  }

  function calculateConversionFee(amount, feeObject) {
    let tFee = feeObject.fee;
    if (feeObject.is_percentage) {
      tFee = amount * feeObject.fee;
      if (feeObject.minimum > 0) {
        tFee = feeObject.minimum > tFee ? feeObject.minimum : tFee;
      }

      if (feeObject.maximum > 0) {
        tFee = feeObject.maximum < tFee ? feeObject.maximum : tFee;
      }

      tFee += feeObject.mandatory_fee;
    }

    let cDiscount = feeObject.discount;
    if (feeObject.is_discount_percentage) {
      cDiscount = tFee * feeObject.discount;
    }

    tFee -= cDiscount;
    return tFee / 100;
  }

  function showCommercials() {
    const conversionRate = exchangeRates[`${sourceCurrency}${destinationCurrency}`];
    const conversionRateText = conversionRate < 1 ? conversionRate : conversionRate.toFixed(2);
    const rLine = `1.00 ${destinationCurrency} = ${sourceCurrency} ${conversionRateText}`;
    setRateLine(rLine);

    if (conversionAmount !== "0.00" || conversionAmount > 0) {
      const tFee = calculateConversionFee(conversionAmount * 100, conversionFee[sourceCurrency]);
      setTransactionFee(tFee.toFixed(2));

      const destAmount = (conversionAmount - tFee) / conversionRate;
      setTargetAmount(destAmount.toFixed(2));
    } else if (targetAmount !== "0.00" || targetAmount > 0) {
      const conVAmt = targetAmount * conversionRate;
      // let tFee = conVAmt * conversionFee;
      // const cAmt = conVAmt + tFee;
      // const finalConversionAmount = Math.max(Math.ceil(cAmt / 5) * 5, cAmt);
      // tFee = finalConversionAmount * conversionFee;

      let tFee = calculateConversionFee(conVAmt * 100, conversionFee[sourceCurrency]);
      const cAmt = conVAmt + tFee;
      const finalConversionAmount = Math.max(Math.ceil(cAmt / 5) * 5, cAmt);
      tFee = calculateConversionFee(finalConversionAmount * 100, conversionFee[sourceCurrency]);

      setTransactionFee(tFee.toFixed(2));
      setConversionAmount(finalConversionAmount.toFixed(2));
    }

    setCalculated(true);
    setConvertButtonText("Proceed");
  }

  async function postConversion() {
    setOpenConversionDialog(false);
    setShowLoading(true);
    const json = {
      source_currency: sourceCurrency,
      destination_currency: destinationCurrency,
      source_amount: conversionAmount * 100,
    };
    await axios
      .post("users/swaps/convert.json", json)
      .then((result) => {
        setShowLoading(false);
        if (result.data.status === "success") {
          setSeverity("success");
          setErrMsg(result.data.data.message);
          setOpen(true);

          setRefreshPage(!refreshPage);
        } else {
          setSeverity("error");
          setErrMsg(result.data.data.message);
          setOpen(true);
        }
      })
      .catch(() => {
        setShowLoading(false);
        setSeverity("error");
        setErrMsg("An error occurred. Make sure you have an active internet connection.");
        setOpen(true);
      });
  }

  function convert() {
    if (sourceCurrency === null) {
      setSeverity("error");
      setErrMsg("Select a source currency");
      setOpen(true);
      return;
    }

    if (destinationCurrency === null) {
      setSeverity("error");
      setErrMsg("Select a destination currency");
      setOpen(true);
      return;
    }

    if (destinationCurrency === sourceCurrency) {
      setSeverity("error");
      setErrMsg("Select different currencies to convert.");
      setOpen(true);
      return;
    }

    if (
      (conversionAmount === null || conversionAmount <= 0) &&
      (targetAmount === null || targetAmount <= 0)
    ) {
      setSeverity("error");
      setErrMsg("Enter an amount in the source or target currency.");
      setOpen(true);
      return;
    }

    if (!calculated) {
      showCommercials();
      return;
    }

    postConversion();
  }

  const [fundingFee, setFundingFee] = useState(0.0);

  function calculateFundingFee(uAmt) {
    setCurrentAmount(uAmt);

    const amount = uAmt * 100;
    const feeObject = vCardFundingFeeObject[currency];
    let tFee = feeObject.fee;
    if (feeObject.is_percentage) {
      tFee = amount * feeObject.fee;
      if (feeObject.minimum > 0) {
        tFee = feeObject.minimum > tFee ? feeObject.minimum : tFee;
      }

      if (feeObject.maximum > 0) {
        tFee = feeObject.maximum < tFee ? feeObject.maximum : tFee;
      }

      tFee += feeObject.mandatory_fee;
    }

    let cDiscount = feeObject.discount;
    if (feeObject.is_discount_percentage) {
      cDiscount = tFee * feeObject.discount;
    }

    tFee -= cDiscount;
    setFundingFee(tFee / 100);
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={showLoading}
        onClick={handleClose}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <Snackbar
        open={open}
        autoHideDuration={6000}
        onClose={handleClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert onClose={handleClose} severity={severity} sx={{ width: "100%" }}>
          {errorMsg}
        </Alert>
      </Snackbar>
      {companyNotification !== null && showCompanyNotification ? (
        <CompanyNotification
          title={companyNotification.title}
          description={companyNotification.description}
        />
      ) : (
        <> </>
      )}
      <Dialog open={openTransferTypeDialog}>
        <DialogTitle>
          <MDBox>Transfer Destination</MDBox>
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            <MDBox>Please select a transfer destination</MDBox>
          </DialogContentText>
          <MDBox sx={{ marginTop: "15px", width: "100%" }}>
            <MDButton
              sx={{ minWidth: 120, width: "100%" }}
              color="dark"
              variant="outlined"
              onClick={() => {
                navigate("/transfers/transfer");
              }}
            >
              <Icon sx={{ fontWeight: "bold" }}>person</Icon>&nbsp; Alero Account &nbsp;
              <Icon sx={{ fontWeight: "bold" }}>chevron_right</Icon>
            </MDButton>
          </MDBox>
          <MDBox sx={{ marginTop: "15px" }}>
            <MDButton
              sx={{ minWidth: 120, width: "100%" }}
              color="dark"
              variant="outlined"
              onClick={() => {
                if (!currencySettings.payout_enabled) {
                  setSeverity("error");
                  setErrMsg(`Bank transfers are not enabled for ${currency}`);
                  setOpen(true);
                  return;
                }
                navigate("/transfers/create");
              }}
            >
              <Icon sx={{ fontWeight: "bold" }}>account_balance</Icon>&nbsp; Bank Account &nbsp;
              <Icon sx={{ fontWeight: "bold" }}>chevron_right</Icon>
            </MDButton>
          </MDBox>
          <MDBox sx={{ marginTop: "15px" }}>
            <MDButton
              sx={{ minWidth: 120, width: "100%" }}
              color="dark"
              variant="outlined"
              onClick={() => {
                navigate("/switch");
              }}
            >
              <Icon sx={{ fontWeight: "bold" }}>paid</Icon>&nbsp; Digital Money &nbsp;
              <Icon sx={{ fontWeight: "bold" }}>chevron_right</Icon>
            </MDButton>
          </MDBox>
        </DialogContent>
        <DialogActions>
          <MDButton
            color="dark"
            variant="contained"
            onClick={() => {
              setOpenTransferTypeDialog(false);
            }}
          >
            Cancel
          </MDButton>
        </DialogActions>
      </Dialog>
      <Dialog open={openNewCardDialog} onClose={handleNewCardDialogClose}>
        <DialogTitle>
          <MDBox>Card Request</MDBox>
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            <MDBox>Enter an alias/nickname for this card</MDBox>
          </DialogContentText>
          <MDBox sx={{ marginTop: "15px" }}>
            <MDInput
              name="cardAlias"
              margin="dense"
              label="Card Alias"
              type="text"
              fullWidth
              variant="outlined"
              onChange={(e) => setCurrentCardAlias(e.target.value)}
            />
          </MDBox>
        </DialogContent>
        <DialogActions>
          <MDButton onClick={handleNewCardDialogClose} color="dark" variant="outlined">
            Cancel
          </MDButton>
          <MDButton
            color="success"
            variant="outlined"
            onClick={() => {
              requestCard();
            }}
          >
            Proceed
          </MDButton>
        </DialogActions>
      </Dialog>
      <Dialog open={openNewBankDialog}>
        <DialogTitle>
          <MDBox>Bank Account Request - {currency}</MDBox>
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            <MDBox>
              We are required by law to capture your ID and current place of residence. Kindly fill
              out the KYC form below.
            </MDBox>
          </DialogContentText>
          <MDBox sx={{ marginTop: "15px" }}>
            <MDInput
              name="address_number"
              margin="dense"
              label="House Number"
              type="text"
              fullWidth
              variant="outlined"
              onChange={(e) => setAddressHouseNumber(e.target.value)}
            />
            <MDInput
              name="address_street"
              margin="dense"
              label="Street Address"
              type="text"
              fullWidth
              variant="outlined"
              onChange={(e) => setAddressStreet(e.target.value)}
            />
            <MDBox>
              <Grid container spacing={1}>
                <Grid item lg={6} md={6} xs={6}>
                  <MDInput
                    name="address_city"
                    margin="dense"
                    label="City"
                    type="text"
                    fullWidth
                    variant="outlined"
                    onChange={(e) => setAddressCity(e.target.value)}
                  />
                </Grid>
                <Grid item lg={6} md={6} xs={6}>
                  <MDInput
                    name="address_state"
                    margin="dense"
                    label="State"
                    type="text"
                    fullWidth
                    variant="outlined"
                    onChange={(e) => setAddressState(e.target.value)}
                  />
                </Grid>
              </Grid>
            </MDBox>
            <MDBox>
              <Grid container spacing={1}>
                <Grid item lg={6} md={6} xs={6}>
                  <MDInput
                    name="address_postal_code"
                    margin="dense"
                    label="Postal Code / Zip"
                    type="text"
                    fullWidth
                    variant="outlined"
                    onChange={(e) => setAddressPostalCode(e.target.value)}
                  />
                </Grid>
                <Grid item lg={6} md={6} xs={6}>
                  <MDInput
                    inputProps={{ maxLength: 2 }}
                    name="address_country"
                    margin="dense"
                    label="Country (ex. NG, US, UK, DE)"
                    type="text"
                    fullWidth
                    variant="outlined"
                    onChange={(e) => setAddressCountry(e.target.value)}
                  />
                </Grid>
              </Grid>
            </MDBox>
            <MDBox mt={3}>
              <Grid container spacing={1}>
                <Grid item lg={6} md={6} xs={6}>
                  <MDBox mt={1}>
                    <FormControl sx={{ minWidth: 120, width: "100%" }}>
                      <InputLabel>ID Type</InputLabel>
                      <Select
                        label="ID Type"
                        name="document_type"
                        sx={{ padding: "12px", paddingLeft: "3px" }}
                        onChange={(e) => {
                          setDocumentType(e.target.value);
                        }}
                      >
                        <MenuItem value="passport">International Passport</MenuItem>
                        <MenuItem value="dl">Driver&apos;s License</MenuItem>
                        <MenuItem value="nin">National ID (NIN)</MenuItem>
                      </Select>
                    </FormControl>
                  </MDBox>
                </Grid>
                <Grid item lg={6} md={6} xs={6}>
                  <MDInput
                    name="document_number"
                    margin="dense"
                    label={documentNumberText}
                    type="text"
                    fullWidth
                    variant="outlined"
                    onChange={(e) => setDocumentNumber(e.target.value)}
                  />
                </Grid>
              </Grid>
            </MDBox>
            {documentType === "dl" || documentType === "passport" ? (
              <MDBox>
                <Grid container spacing={1}>
                  <Grid item lg={6} md={6} xs={6}>
                    <MDInput
                      type="date"
                      name="document_iss_date"
                      margin="dense"
                      label="Date Issued"
                      fullWidth
                      variant="outlined"
                      onChange={(e) => setDocumentIssDate(e.target.value)}
                    />
                  </Grid>
                  <Grid item lg={6} md={6} xs={6}>
                    <MDInput
                      type="date"
                      name="document_exp_date"
                      margin="dense"
                      label="Expiry Date"
                      fullWidth
                      variant="outlined"
                      onChange={(e) => setDocumentExpDate(e.target.value)}
                    />
                  </Grid>
                </Grid>
              </MDBox>
            ) : (
              <> </>
            )}
            {["nin"].includes(documentType) ? (
              <MDBox>
                <MDInput
                  type="date"
                  name="document_iss_date"
                  margin="dense"
                  label="Registration Date"
                  fullWidth
                  variant="outlined"
                  onChange={(e) => setDocumentIssDate(e.target.value)}
                />
              </MDBox>
            ) : (
              <> </>
            )}
            <MDBox mt={3}>
              <MDTypography variant="caption" pl={1} fontWeight="medium">
                Upload ID Document (PDF, JPEG, PNG)
              </MDTypography>
              <MDInput
                name="document_file"
                margin="dense"
                type="file"
                fullWidth
                variant="outlined"
                onChange={(e) => {
                  handleFileInputChange(e, "DF");
                }}
              />
            </MDBox>
            {documentType === "dl" ? (
              <MDBox mt={1}>
                <MDTypography variant="caption" pl={1} fontWeight="medium">
                  Upload ID Document Back (PDF, JPEG, PNG)
                </MDTypography>
                <MDInput
                  name="document_file_2"
                  margin="dense"
                  type="file"
                  fullWidth
                  variant="outlined"
                  onChange={(e) => {
                    handleFileInputChange(e, "DB");
                  }}
                />
              </MDBox>
            ) : (
              <> </>
            )}
            <MDBox mt={1.5}>
              <MDTypography variant="caption" pl={1} fontWeight="medium" mb={1}>
                Upload Utility Bill / Bank Statement
              </MDTypography>
              <MDBox mt={0.5}>
                <Grid container spacing={1}>
                  <Grid item lg={3} md={3} xs={12}>
                    <MDBox mt={1}>
                      <FormControl sx={{ minWidth: 120, width: "100%" }}>
                        <InputLabel>File Type</InputLabel>
                        <Select
                          label="File type"
                          name="address_file_type"
                          sx={{ padding: "12px", paddingLeft: "3px" }}
                          onChange={(e) => {
                            setAddressFileType(e.target.value);
                          }}
                        >
                          <MenuItem value="utilityBill">Utility Bill</MenuItem>
                          <MenuItem value="bankStatement">Bank Statement</MenuItem>
                        </Select>
                      </FormControl>
                    </MDBox>
                  </Grid>
                  <Grid item lg={9} md={9} xs={12}>
                    <MDInput
                      name="utility_bill"
                      margin="dense"
                      type="file"
                      fullWidth
                      variant="outlined"
                      onChange={(e) => {
                        handleFileInputChange(e, "A");
                      }}
                    />
                  </Grid>
                </Grid>
              </MDBox>
              <MDBox>
                <MDTypography variant="caption" color="warning" pl={1} mt={-1} fontWeight="medium">
                  Note! Document should be within 3 months.
                </MDTypography>
              </MDBox>
            </MDBox>
          </MDBox>
        </DialogContent>
        <DialogActions>
          <MDButton
            onClick={() => {
              setOpenNewBankDialog(false);
            }}
            color="dark"
            variant="outlined"
          >
            Cancel
          </MDButton>
          <MDButton
            color="success"
            variant="outlined"
            onClick={() => {
              requestAccount();
            }}
          >
            Proceed
          </MDButton>
        </DialogActions>
      </Dialog>
      <Dialog open={openFundDialog} onClose={handleFundDialogClose}>
        <form onSubmit={handleFundCardSubmit}>
          <DialogTitle>
            <MDBox>Fund Card</MDBox>
          </DialogTitle>
          <DialogContent>
            <DialogContentText>
              <MDBox>Enter an amount to top up your card</MDBox>
            </DialogContentText>
            <MDBox sx={{ marginTop: "15px" }}>
              <MDInput
                name="amount"
                margin="dense"
                label={`${currency} Amount`}
                type="decimal"
                fullWidth
                variant="outlined"
                onChange={(e) => calculateFundingFee(e.target.value)}
              />
            </MDBox>
            <MDBox mt={1}>
              <MDTypography
                pr={1}
                variant="caption"
                color="dark"
                fontWeight="medium"
                sx={{ display: "flex", justifyContent: "flex-end" }}
              >
                Fee: {currency} {fundingFee}
              </MDTypography>
            </MDBox>
          </DialogContent>
          <DialogActions>
            <MDButton onClick={handleFundDialogClose} color="dark" variant="outlined">
              Cancel
            </MDButton>
            <MDButton color="success" variant="outlined" type="submit">
              Fund
            </MDButton>
          </DialogActions>
        </form>
      </Dialog>

      <Dialog open={openWithdrawDialog} onClose={handleWithdrawDialogClose}>
        <form onSubmit={handleWithdrawCardSubmit}>
          <DialogTitle>
            <MDBox>Withdraw Funds</MDBox>
          </DialogTitle>
          <DialogContent>
            <DialogContentText>
              <MDBox>Enter an amount to withdraw to your balance</MDBox>
            </DialogContentText>
            <MDBox sx={{ marginTop: "15px" }}>
              <MDInput
                name="amount"
                margin="dense"
                label={`${currency} Amount`}
                type="decimal"
                fullWidth
                variant="outlined"
                onChange={(e) => setCurrentAmount(e.target.value)}
              />
            </MDBox>
            <MDBox mt={1}>
              <Alert severity="warning">
                <small>
                  {currency} {vCardWithdrawalFee / 100} withdrawal fee will be applied.
                </small>
              </Alert>
            </MDBox>
          </DialogContent>
          <DialogActions>
            <MDButton onClick={handleWithdrawDialogClose} color="dark" variant="outlined">
              Cancel
            </MDButton>
            <MDButton color="warning" variant="outlined" type="submit">
              Withdraw
            </MDButton>
          </DialogActions>
        </form>
      </Dialog>
      <Dialog open={openCancelDialog}>
        <DialogTitle>
          <MDBox>Delete Card?</MDBox>
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            <MDBox>
              Are you sure you want to delete this card? Any funds left will be lost if you proceed.
            </MDBox>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <MDButton
            onClick={() => {
              setCancelDialog(false);
            }}
            color="dark"
            variant="outlined"
          >
            Cancel
          </MDButton>
          <MDButton
            color="warning"
            variant="outlined"
            onClick={() => {
              setCancelDialog(false);
              terminateCard();
            }}
          >
            Delete
          </MDButton>
        </DialogActions>
      </Dialog>
      <Dialog open={openDepositDialog}>
        <DialogTitle>
          <MDBox>Deposit {currency}</MDBox>
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            <MDBox>
              Send money to any of your {currency} bank accounts to fund your {currency} balance.
            </MDBox>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <MDButton
            onClick={() => {
              setDepositDialog(false);
            }}
            color="dark"
            variant="outlined"
          >
            Got it
          </MDButton>
        </DialogActions>
      </Dialog>
      {commericals != null ? (
        <>
          <Dialog open={openVCardDialog}>
            <DialogTitle>
              <MDBox>Request Card</MDBox>
            </DialogTitle>
            <DialogContent>
              <DialogContentText>
                <MDBox pl={2}>
                  <ul>
                    <li>
                      You will be charged{" "}
                      <b>
                        {currency} {commericals.vcard.fee / 100}
                      </b>{" "}
                      card fee.
                    </li>
                    <li>
                      A minimum of{" "}
                      <b>
                        {currency} {commericals.vcard.prefund / 100}
                      </b>{" "}
                      is required to pre-fund your card.
                    </li>
                  </ul>
                </MDBox>
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <MDButton
                onClick={() => {
                  setVCardDialog(false);
                }}
                color="dark"
                variant="outlined"
              >
                Cancel
              </MDButton>
              <MDButton
                color="success"
                variant="outlined"
                onClick={() => {
                  if (!currencySettings.vcard_enabled) {
                    setSeverity("error");
                    setErrMsg(`Virtual cards are not enabled for ${currency}`);
                    setOpen(true);
                    return;
                  }

                  setVCardDialog(false);
                  // requestCard();
                  setOpenNewCardDialog(true);
                }}
              >
                Request Card
              </MDButton>
            </DialogActions>
          </Dialog>
          <Dialog open={openVBankDialog}>
            <DialogTitle>
              <MDBox>Request Account</MDBox>
            </DialogTitle>
            <DialogContent>
              <DialogContentText>
                {currency !== "NGN" ? (
                  <MDBox pl={2}>
                    <ul>
                      <li>
                        You will be charged{" "}
                        <b>
                          {currency} {(commericals.vbank.fee / 100).toFixed(2)}
                        </b>{" "}
                        account opening fee.
                      </li>
                      {commericals.vbank.renewal ? (
                        <li>
                          A monthly renewal fee of{" "}
                          <b>
                            {currency} {(commericals.vbank.renewal / 100).toFixed(2)}
                          </b>{" "}
                          is required to maintain your account.
                        </li>
                      ) : (
                        <> </>
                      )}
                    </ul>
                  </MDBox>
                ) : (
                  <MDBox>
                    <Alert severity="info">{currency} bank accounts are free forever!</Alert>
                  </MDBox>
                )}
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <MDButton
                onClick={() => {
                  setVBankDialog(false);
                }}
                color="dark"
                variant="outlined"
              >
                Cancel
              </MDButton>
              <MDButton
                color="success"
                variant="outlined"
                onClick={() => {
                  setVBankDialog(false);
                  if (currency !== "NGN") {
                    setOpenNewBankDialog(true);
                  } else {
                    requestAccount();
                  }
                }}
              >
                Proceed
              </MDButton>
            </DialogActions>
          </Dialog>
        </>
      ) : (
        <> </>
      )}
      <Dialog open={openDepositOptionsDialog}>
        <DialogTitle>
          <MDBox>Deposit Options</MDBox>
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            <MDBox>Please select a deposit option.</MDBox>
          </DialogContentText>
          <MDBox sx={{ marginTop: "15px", width: "100%" }}>
            <MDButton
              sx={{ minWidth: 120, width: "100%" }}
              color="dark"
              variant="outlined"
              onClick={() => {
                setOpenDepositOptionsDialog(false);
                if (!currencySettings.vbank_enabled) {
                  setSeverity("error");
                  setErrMsg(`Deposits are not enabled for ${currency}`);
                  setOpen(true);
                  return;
                }
                setDepositDialog(true);
              }}
            >
              <Icon sx={{ fontWeight: "bold" }}>account_balance</Icon>&nbsp; Bank Transfer &nbsp;
              <Icon sx={{ fontWeight: "bold" }}>chevron_right</Icon>
            </MDButton>
          </MDBox>
          <MDBox sx={{ marginTop: "15px" }}>
            <MDButton
              sx={{ minWidth: 120, width: "100%" }}
              color="dark"
              variant="outlined"
              onClick={() => {
                navigate("/switch");
              }}
            >
              <Icon sx={{ fontWeight: "bold" }}>paid</Icon>&nbsp; Digital Money &nbsp;
              <Icon sx={{ fontWeight: "bold" }}>chevron_right</Icon>
            </MDButton>
          </MDBox>
        </DialogContent>
        <DialogActions>
          <MDButton
            color="dark"
            variant="contained"
            onClick={() => {
              setOpenDepositOptionsDialog(false);
            }}
          >
            Cancel
          </MDButton>
        </DialogActions>
      </Dialog>
      <Dialog open={openConversionTypeDialog}>
        <DialogTitle>
          <MDBox>Convert Options</MDBox>
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            <MDBox>Please select a conversion option.</MDBox>
          </DialogContentText>
          <MDBox sx={{ marginTop: "15px", width: "100%" }}>
            <MDButton
              sx={{ minWidth: 120, width: "100%" }}
              color="dark"
              variant="outlined"
              onClick={() => {
                setOpenConversionTypeDialog(false);
                resetSwapForm();
                setOpenConversionDialog(false);
                navigate("/convert");
              }}
            >
              <Icon sx={{ fontWeight: "bold" }}>currency_exchange</Icon>&nbsp; Instant Conversion
              &nbsp;
              <Icon sx={{ fontWeight: "bold" }}>chevron_right</Icon>
            </MDButton>
          </MDBox>
          <MDBox sx={{ marginTop: "15px" }}>
            <MDButton
              sx={{ minWidth: 120, width: "100%" }}
              color="dark"
              variant="outlined"
              onClick={() => {
                navigate("/p2p");
              }}
            >
              <Icon sx={{ fontWeight: "bold" }}>people_alt</Icon>&nbsp; P2P MarketPlace &nbsp;
              <Icon sx={{ fontWeight: "bold" }}>chevron_right</Icon>
            </MDButton>
          </MDBox>
          <MDBox sx={{ marginTop: "15px" }}>
            <MDButton
              sx={{ minWidth: 120, width: "100%" }}
              color="dark"
              variant="outlined"
              onClick={() => {
                navigate("/switch");
              }}
            >
              <Icon sx={{ fontWeight: "bold" }}>paid</Icon>&nbsp; Alero Switch &nbsp;
              <Icon sx={{ fontWeight: "bold" }}>chevron_right</Icon>
            </MDButton>
          </MDBox>
          <MDBox sx={{ marginTop: "15px" }}>
            <MDButton
              sx={{ minWidth: 120, width: "100%" }}
              color="dark"
              variant="outlined"
              onClick={() => {
                navigate("/giftcards");
              }}
            >
              <Icon sx={{ fontWeight: "bold" }}>redeem</Icon>&nbsp; Gift cards&nbsp;
              <Icon sx={{ fontWeight: "bold" }}>chevron_right</Icon>
            </MDButton>
          </MDBox>
        </DialogContent>
        <DialogActions>
          <MDButton
            color="dark"
            variant="contained"
            onClick={() => {
              setOpenConversionTypeDialog(false);
            }}
          >
            Cancel
          </MDButton>
        </DialogActions>
      </Dialog>
      <Dialog open={openConversionDialog} fullWidth="xs" maxWidth="xs">
        <DialogTitle>
          <MDBox>Convert Funds</MDBox>
        </DialogTitle>
        <DialogContent>
          <MDBox sx={{ marginTop: "15px" }}>
            <MDBox mt={1}>
              <FormControl sx={{ minWidth: 120, width: "100%" }}>
                <InputLabel>From</InputLabel>
                <Select
                  label="From"
                  onChange={(e) => {
                    setConvertButtonText("Calculate");
                    setCalculated(false);
                    setSourceCurrency(e.target.value);
                  }}
                  sx={{ padding: "12px", paddingLeft: "3px" }}
                >
                  {
                    // --> Notice below changes
                    currencies.length > 0 && // --> Changes from `options.length ? ...` to `options.length > 0 && ...`
                      currencies.map((record) => {
                        if (record.swap_enabled) {
                          return (
                            <MenuItem value={record.currency_code}>{record.currency_code}</MenuItem>
                          );
                        }
                        return null;
                      })
                  }
                </Select>
              </FormControl>
            </MDBox>
            <MDBox mt={3}>
              <FormControl sx={{ minWidth: 120, width: "100%" }}>
                <InputLabel>To</InputLabel>
                <Select
                  label="To"
                  onChange={(e) => {
                    setCalculated(false);
                    setConvertButtonText("Calculate");
                    setDestinationCurrency(e.target.value);
                  }}
                  sx={{ padding: "12px", paddingLeft: "3px" }}
                >
                  {
                    // --> Notice below changes
                    currencies.length > 0 && // --> Changes from `options.length ? ...` to `options.length > 0 && ...`
                      currencies.map((record) => {
                        if (record.swap_enabled) {
                          return (
                            <MenuItem value={record.currency_code}>{record.currency_code}</MenuItem>
                          );
                        }
                        return null;
                      })
                  }
                </Select>
              </FormControl>
            </MDBox>
            <MDBox mt={2}>
              <MDInput
                name="source_amount"
                margin="dense"
                label={amountPlaceholder}
                value={conversionAmount}
                type="number"
                fullWidth
                variant="outlined"
                onChange={(e) => {
                  setCalculated(false);
                  setConvertButtonText("Calculate");
                  setConversionAmount(e.target.value);
                  setTargetAmount("0.00");
                }}
              />
            </MDBox>
            <MDBox mt={2}>
              <MDInput
                name="target_amount"
                margin="dense"
                label={amountPlaceholder1}
                value={targetAmount}
                type="number"
                fullWidth
                variant="outlined"
                onChange={(e) => {
                  setCalculated(false);
                  setConvertButtonText("Calculate");
                  setTargetAmount(e.target.value);
                  setConversionAmount("0.00");
                }}
              />
            </MDBox>
            {calculated ? (
              <MDBox mt={1}>
                <Alert severity="info" sx={{ width: "100%" }}>
                  <MDBox>
                    <small variant="caption" fontWeight="heavy">
                      {rateLine}
                    </small>
                  </MDBox>
                  <MDBox>
                    <small variant="caption" fontWeight="medium">
                      {`Fee: ${sourceCurrency} ${transactionFee}`}
                    </small>
                  </MDBox>
                  {/* <MDBox>
                    <small variant="caption" fontWeight="medium">
                      {`You get: ${destinationCurrency} ${destinationAmount}`}
                    </small>
                  </MDBox> */}
                </Alert>
              </MDBox>
            ) : (
              <> </>
            )}
          </MDBox>
        </DialogContent>
        <DialogActions>
          <MDButton
            onClick={() => {
              setConvertButtonText("Calculate");
              setOpenConversionDialog(false);
            }}
            color="dark"
            variant="outlined"
          >
            Cancel
          </MDButton>
          <MDButton
            color="success"
            variant="outlined"
            onClick={() => {
              convert();
            }}
          >
            {convertButtonText}
          </MDButton>
        </DialogActions>
      </Dialog>
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            {!isVerified ? (
              <MDBox mb={5} mr={1}>
                <Alert severity="error" sx={{ width: "100%" }}>
                  <AlertTitle>ID Verification Required</AlertTitle>
                  Go to your profile page. <Link to="/profile">My Profile</Link>
                </Alert>
              </MDBox>
            ) : (
              <> </>
            )}
            <Grid container spacing={3}>
              <Grid item xs={12} md={4} lg={4}>
                <MDBox mb={1.5}>
                  <BalanceOverviewCard
                    idx="balance"
                    color="dark"
                    icon="account_balance_wallet"
                    // currencyCode={currency}
                    symbol={currencySettings != null ? currencySettings.symbol : currency}
                    amount={balance}
                    title="Current Balance"
                  />
                </MDBox>
              </Grid>
              <Grid item xs={12} md={8} lg={8}>
                <Card sx={{ width: "100%" }}>
                  <MDBox p={3.15}>
                    <Grid container spacing={2}>
                      <Grid item xs={6} md={4} lg={4}>
                        <MDBox display="flex" justifyContent="center">
                          <MDButton
                            variant="outlined"
                            color="success"
                            onClick={() => {
                              setOpenDepositOptionsDialog(true);
                            }}
                          >
                            <Icon sx={{ fontWeight: "bold" }}>add</Icon>
                            &nbsp;Deposit {currency}
                          </MDButton>
                        </MDBox>
                      </Grid>
                      <Grid item xs={6} md={4} lg={4}>
                        <MDBox display="flex" justifyContent="center">
                          <MDButton
                            variant="outlined"
                            color="error"
                            onClick={() => {
                              // navigate("/payouts/create");
                              setOpenTransferTypeDialog(true);
                            }}
                          >
                            <Icon sx={{ fontWeight: "bold" }}>send</Icon>
                            &nbsp;Withdraw {currency}
                          </MDButton>
                        </MDBox>
                      </Grid>
                      <Grid item xs={6} md={4} lg={4}>
                        <MDBox display="flex" justifyContent="center">
                          <MDButton
                            variant="outlined"
                            color="info"
                            onClick={() => {
                              setCalculated(false);
                              setOpenConversionTypeDialog(true);
                            }}
                          >
                            <Icon sx={{ fontWeight: "bold" }}>currency_exchange</Icon>
                            &nbsp;Convert {currency}
                          </MDButton>
                        </MDBox>
                      </Grid>
                    </Grid>
                  </MDBox>
                </Card>
              </Grid>
              {/* <Grid item xs={12} md={4} lg={4}>
                <MDBox mb={1.5}>
                  <BalanceOverviewCard
                    idx="credit"
                    color="success"
                    icon="add"
                    currencyCode={currency}
                    amount={credit}
                    title="Total Received"
                  />
                </MDBox>
              </Grid>
              <Grid item xs={12} md={4} lg={4}>
                <MDBox mb={1.5}>
                  <BalanceOverviewCard
                    idx="balance"
                    color="error"
                    icon="remove"
                    currencyCode={currency}
                    amount={debit}
                    title="Total Spent"
                  />
                </MDBox>
              </Grid> */}
            </Grid>
          </Grid>
        </Grid>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6} lg={6}>
                {/* <Card sx={{ width: "100%", marginTop: "35px" }}>
                  <MDBox p={2}>
                    <Grid container spacing={3}>
                      <Grid item xs={6} md={4} lg={4}>
                        <MDBox lineHeight={1}>
                          <MDButton
                            variant="outlined"
                            color="success"
                            onClick={() => {
                              if (!currencySettings.vbank_enabled) {
                                setSeverity("error");
                                setErrMsg(`Deposits are not enabled for ${currency}`);
                                setOpen(true);
                                return;
                              }
                              setDepositDialog(true);
                            }}
                          >
                            <Icon sx={{ fontWeight: "bold" }}>add</Icon>
                            &nbsp;Deposit {currency}
                          </MDButton>
                        </MDBox>
                      </Grid>
                      <Grid item xs={6} md={4} lg={4}>
                        <MDBox lineHeight={1}>
                          <MDButton
                            variant="outlined"
                            color="error"
                            onClick={() => {
                              // navigate("/payouts/create");
                              setOpenTransferTypeDialog(true);
                            }}
                          >
                            <Icon sx={{ fontWeight: "bold" }}>send</Icon>
                            &nbsp;Withdraw {currency}
                          </MDButton>
                        </MDBox>
                      </Grid>
                      <Grid item xs={6} md={4} lg={4}>
                        <MDBox lineHeight={1}>
                          <MDButton
                            variant="outlined"
                            color="info"
                            onClick={() => {
                              setCalculated(false);
                              setOpenConversionTypeDialog(true);
                            }}
                          >
                            <Icon sx={{ fontWeight: "bold" }}>currency_exchange</Icon>
                            &nbsp;Convert {currency}
                          </MDButton>
                        </MDBox>
                      </Grid>
                    </Grid>
                  </MDBox>
                </Card> */}

                <Card sx={{ width: "100%", marginTop: "35px" }}>
                  <MDBox
                    pt={2}
                    px={2}
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <MDTypography variant="h6" fontWeight="medium">
                      My Bank Accounts
                    </MDTypography>
                    <MDButton
                      variant="contained"
                      color="dark"
                      onClick={() => {
                        if (!currencySettings.vbank_enabled) {
                          setSeverity("error");
                          setErrMsg(`Virtual bank accounts are not enabled for ${currency}`);
                          setOpen(true);
                          return;
                        }
                        setVBankDialog(true);
                      }}
                    >
                      <Icon sx={{ fontWeight: "bold" }}>add</Icon>
                      &nbsp;request account
                    </MDButton>
                  </MDBox>
                  {vbanks <= 0 ? (
                    <MDBox pt={3} pb={2} px={2}>
                      <MDBox
                        component="ul"
                        display="flex"
                        flexDirection="column"
                        p={0}
                        m={0}
                        sx={{ listStyle: "none" }}
                      >
                        <small>No bank accounts found.</small>
                      </MDBox>
                    </MDBox>
                  ) : (
                    <MDBox pt={3} pb={2} px={0}>
                      <MDBox
                        display="flex"
                        flexDirection="column"
                        p={0}
                        m={0}
                        sx={{ listStyle: "none" }}
                      >
                        {vbanks.map((record, index) => (
                          <MDBox
                            idx={index}
                            borderRadius="lg"
                            p={3}
                            sx={{
                              marginLeft: "10px",
                              marginTop: "5px",
                              marginRight: "10px",
                              marginBottom: "15px",
                              border: ({ borders: { borderWidth, borderColor } }) =>
                                `${borderWidth[1]} solid ${borderColor}`,
                            }}
                          >
                            <Grid container spacing={1} xs={12} lg={12} md={12}>
                              <Grid item xs={12} lg={6} md={6}>
                                <MDBox mb={0.5} lineHeight={0}>
                                  <MDTypography variant="caption" color="text">
                                    Currency:
                                  </MDTypography>
                                </MDBox>
                                <MDBox mb={1} lineHeight={0}>
                                  <MDTypography
                                    variant="caption"
                                    fontWeight="medium"
                                    textTransform="capitalize"
                                  >
                                    {currency}
                                  </MDTypography>
                                </MDBox>
                              </Grid>
                              {Object.keys(record.details).map((key) => (
                                <Grid item xs={12} lg={6} md={6}>
                                  <MDBox mb={0.5} lineHeight={0}>
                                    <MDTypography variant="caption" color="text">
                                      {key}:&nbsp;&nbsp;&nbsp;
                                    </MDTypography>
                                  </MDBox>
                                  <MDBox mb={1} lineHeight={0}>
                                    <MDTypography
                                      variant="caption"
                                      fontWeight="medium"
                                      textTransform="capitalize"
                                    >
                                      {record.details[key]} &nbsp;
                                      <Icon
                                        onClick={() => {
                                          navigator.clipboard.writeText(record.details[key]);
                                          const wrd =
                                            key[0].toUpperCase() + key.slice(1).toLowerCase();
                                          setSeverity("success");
                                          setErrMsg(`${wrd} copied!`);
                                          setOpen(true);
                                        }}
                                      >
                                        copy
                                      </Icon>
                                    </MDTypography>
                                  </MDBox>
                                </Grid>
                              ))}
                            </Grid>
                          </MDBox>
                        ))}
                      </MDBox>
                    </MDBox>
                  )}
                </Card>

                {showCards ? (
                  <Card sx={{ width: "100%", marginTop: "35px", paddingBottom: "15px" }}>
                    <MDBox
                      pt={2}
                      px={2}
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      marginBottom="15px"
                    >
                      <MDTypography variant="h6" fontWeight="medium">
                        My Virtual Cards
                      </MDTypography>
                      <MDButton
                        variant="contained"
                        color="dark"
                        onClick={() => {
                          // requestCard();
                          if (!currencySettings.vcard_enabled) {
                            setSeverity("error");
                            setErrMsg(`Virtual cards are not enabled for ${currency}`);
                            setOpen(true);
                            return;
                          }
                          setVCardDialog(true);
                        }}
                      >
                        <Icon sx={{ fontWeight: "bold" }}>add</Icon>
                        &nbsp;request card
                      </MDButton>
                    </MDBox>
                    {vcards.length <= 0 ? (
                      <MDBox pt={3} pb={2} px={2}>
                        <MDBox
                          component="ul"
                          display="flex"
                          flexDirection="column"
                          p={0}
                          m={0}
                          sx={{ listStyle: "none" }}
                        >
                          <small>No cards found.</small>
                        </MDBox>
                      </MDBox>
                    ) : (
                      vcards.map((card) => (
                        <MDBox
                          borderRadius="lg"
                          p={3}
                          sx={{
                            marginLeft: "10px",
                            marginTop: "5px",
                            marginRight: "10px",
                            marginBottom: "15px",
                            border: ({ borders: { borderWidth, borderColor } }) =>
                              `${borderWidth[1]} solid ${borderColor}`,
                          }}
                        >
                          <Grid container spacing={3}>
                            <Grid item xs={12} lg={12} md={12}>
                              <MDBox mb={3} sx={{ textAlign: "left" }}>
                                <MDTypography
                                  variant="h6"
                                  fontWeight="medium"
                                  textTransform="capitalize"
                                >
                                  {card.title}
                                </MDTypography>
                              </MDBox>
                              <MDBox
                                display="flex"
                                justifyContent="space-between"
                                alignItems="center"
                              >
                                <MDBox
                                  component="img"
                                  src={
                                    card.details.brand === "mastercard" ? masterCardLogo : visaLogo
                                  }
                                  alt="virtual card"
                                  width="10%"
                                  mr={2}
                                />
                                <MDTypography variant="h5" fontWeight="medium">
                                  {card.details.pan.substring(0, 4)}
                                  &nbsp;{"   "}&nbsp;
                                  {card.details.pan.substring(4, 8)}
                                  &nbsp;{"   "}&nbsp;
                                  {card.details.pan.substring(8, 12)}
                                  &nbsp;{"   "}&nbsp;
                                  {card.details.pan.substring(12, 16)}
                                </MDTypography>
                              </MDBox>
                            </Grid>
                            <Grid item xs={12} lg={12} md={12}>
                              <Grid container spacing={3}>
                                <Grid item xs={12} lg={12} md={12}>
                                  <MDBox
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="space-between"
                                  >
                                    <MDBox mr={3} lineHeight={1}>
                                      <MDTypography
                                        variant="button"
                                        fontWeight="regular"
                                        opacity={0.8}
                                      >
                                        Card Holder
                                      </MDTypography>
                                      <MDTypography
                                        variant="h6"
                                        fontWeight="medium"
                                        textTransform="capitalize"
                                      >
                                        {card.details.cardholder}
                                      </MDTypography>
                                    </MDBox>
                                    <MDBox mr={3} lineHeight={1}>
                                      <MDTypography
                                        variant="button"
                                        fontWeight="regular"
                                        opacity={0.8}
                                      >
                                        Expires
                                      </MDTypography>
                                      <MDTypography variant="h6" fontWeight="medium">
                                        {card.details.exp_date}
                                      </MDTypography>
                                    </MDBox>
                                    <MDBox lineHeight={1}>
                                      <MDTypography
                                        variant="button"
                                        fontWeight="regular"
                                        opacity={0.8}
                                      >
                                        CVV
                                      </MDTypography>
                                      <MDTypography variant="h6" fontWeight="medium">
                                        {card.details.cvv}
                                      </MDTypography>
                                    </MDBox>
                                  </MDBox>
                                </Grid>
                              </Grid>
                            </Grid>
                            <Grid item xs={12} lg={12} md={12}>
                              <Grid container spacing={3}>
                                <Grid item xs={12} lg={12} md={12}>
                                  <MDBox
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="space-between"
                                  >
                                    <MDBox mr={3} lineHeight={1}>
                                      <MDTypography
                                        variant="button"
                                        fontWeight="regular"
                                        opacity={0.8}
                                      >
                                        Billing Address
                                      </MDTypography>
                                      <MDTypography variant="h6" fontWeight="medium">
                                        {card.details.billing_address}
                                      </MDTypography>
                                    </MDBox>
                                    <MDBox lineHeight={1}>
                                      <MDTypography
                                        variant="button"
                                        fontWeight="regular"
                                        opacity={0.8}
                                      >
                                        Postal Code
                                      </MDTypography>
                                      <MDTypography variant="h6" fontWeight="medium" align="right">
                                        {card.details.billing_postal_code}
                                      </MDTypography>
                                    </MDBox>
                                  </MDBox>
                                </Grid>
                              </Grid>
                            </Grid>
                            <Grid item xs={12} lg={12} md={12}>
                              <MDBox marginTop="5px" alignItems="right">
                                <MDBox
                                  display="flex"
                                  alignItems="center"
                                  justifyContent="space-between"
                                  color={darkMode ? "white" : "dark"}
                                  sx={{ width: "100%", marginRight: "10px" }}
                                >
                                  <MDBox lineHeight={1}>
                                    <Tooltip title="View Card" placement="top">
                                      <MDButton
                                        variant="outlined"
                                        color="info"
                                        iconOnly
                                        circular
                                        onClick={() => {
                                          navigate(`/cards/${card.reference}`);
                                        }}
                                      >
                                        <Icon sx={{ cursor: "pointer" }} fontSize="small">
                                          visibility
                                        </Icon>
                                      </MDButton>
                                    </Tooltip>
                                    &nbsp;&nbsp;&nbsp;
                                    <Tooltip title="Fund Card" placement="top">
                                      <MDButton
                                        variant="outlined"
                                        color="success"
                                        iconOnly
                                        circular
                                        onClick={() => {
                                          handleOpenFundDialog(card.reference);
                                        }}
                                      >
                                        <Icon sx={{ cursor: "pointer" }} fontSize="small">
                                          add
                                        </Icon>
                                      </MDButton>
                                    </Tooltip>
                                    &nbsp;&nbsp;&nbsp;
                                    <Tooltip title="Withdraw Funds" placement="top">
                                      <MDButton
                                        variant="outlined"
                                        color="error"
                                        iconOnly
                                        circular
                                        onClick={() => {
                                          handleOpenWithdrawDialog(card.reference);
                                        }}
                                      >
                                        <Icon sx={{ cursor: "pointer" }} fontSize="small">
                                          remove
                                        </Icon>
                                      </MDButton>
                                    </Tooltip>
                                  </MDBox>
                                  <MDBox lineHeight={1}>
                                    <Tooltip title="Delete Card" placement="top">
                                      <MDButton
                                        variant="outlined"
                                        color="dark"
                                        iconOnly
                                        circular
                                        // onClick={async () => {
                                        //   const result = await confirm("Are you sure?", {
                                        //     labels: {
                                        //       confirmable: "Confirm",
                                        //       cancellable: "Cancel",
                                        //     },
                                        //   });
                                        //   if (result) {
                                        //     terminateCard(card.reference);
                                        //   }
                                        // }}
                                        onClick={() => {
                                          // terminateCard(card.reference);
                                          setCurrentCardReference(card.reference);
                                          setCancelDialog(true);
                                        }}
                                      >
                                        <Icon
                                          sx={{ cursor: "pointer" }}
                                          fontSize="small"
                                          color="dark"
                                        >
                                          close
                                        </Icon>
                                      </MDButton>
                                    </Tooltip>
                                  </MDBox>
                                </MDBox>
                              </MDBox>
                            </Grid>
                          </Grid>
                        </MDBox>
                      ))
                    )}
                  </Card>
                ) : (
                  <> </>
                )}
              </Grid>
              <Grid item xs={12} md={6} lg={6}>
                <Card sx={{ width: "100%", marginTop: "35px" }}>
                  <MDBox
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    pt={3}
                    px={2}
                  >
                    <MDTypography variant="h6" fontWeight="medium" textTransform="capitalize">
                      Recent Transactions
                    </MDTypography>
                  </MDBox>
                  {transactions === null ? (
                    <MDBox pt={3} pb={2} px={2}>
                      <MDBox
                        component="ul"
                        display="flex"
                        flexDirection="column"
                        p={0}
                        m={0}
                        sx={{ listStyle: "none" }}
                      >
                        No recent transactions found
                      </MDBox>
                    </MDBox>
                  ) : (
                    <MDBox pt={3} pb={2} px={2}>
                      <MDBox
                        component="ul"
                        display="flex"
                        flexDirection="column"
                        p={0}
                        m={0}
                        sx={{ listStyle: "none" }}
                      >
                        {transactions.map((record, index) => (
                          <Grid item xs={12} md={12} lg={12}>
                            <MDBox mb={1.5}>
                              <Transaction
                                idx={index}
                                currency={
                                  currencySettings != null ? currencySettings.symbol : currency
                                }
                                entryType={record.entry_type}
                                narration={record.narration}
                                amount={record.amount}
                                created={record.created}
                              />
                            </MDBox>
                          </Grid>
                        ))}
                      </MDBox>
                    </MDBox>
                  )}
                </Card>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default Balance;
